import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from './api';
import { AVAILABLE_LANGUAGES } from './utils/languages';

const AIInsightsManager = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    recommendation: '',
    icon: '',
    additionalCondition: ''
  });
  const [languageTab, setLanguageTab] = useState('english');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    byType: {},
    byCategory: {}
  });

  

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      // Fetch all insights by setting a high limit
      const response = await api.get('/ai-insights', {
        params: {
          limit: 1000 // Get all insights
        }
      });
      setInsights(response.data.insights);
      calculateStatistics(response.data.insights);
    } catch (error) {
      console.error('Error fetching insights:', error);
      showSnackbar('Failed to fetch AI insights', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (insightsList) => {
    const stats = {
      total: insightsList.length,
      active: insightsList.length, // All insights are now active
      byType: {},
      byCategory: {}
    };

    setStatistics(stats);
  };

  const handleCreateOrUpdate = async () => {
    try {
      setSaving(true);
      const payload = { ...formData };

      if (selectedInsight) {
        await api.put(`/ai-insights/${selectedInsight.id}`, payload);
        showSnackbar('AI insight updated successfully', 'success');
      } else {
        await api.post('/ai-insights', payload);
        showSnackbar('AI insight created successfully', 'success');
      }

      setOpenDialog(false);
      resetForm();
      fetchInsights();
    } catch (error) {
      console.error('Error saving insight:', error);
      showSnackbar(error.response?.data?.error || 'Failed to save AI insight', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this AI insight?')) {
      try {
        await api.delete(`/ai-insights/${id}`);
        showSnackbar('AI insight deleted successfully', 'success');
        fetchInsights();
      } catch (error) {
        console.error('Error deleting insight:', error);
        showSnackbar('Failed to delete AI insight', 'error');
      }
    }
  };

  // Removed handleToggleActive as isActive field no longer exists

  const handleEdit = (insight) => {
    setSelectedInsight(insight);
    setFormData({
      title: insight.title,
      message: insight.message,
      recommendation: insight.recommendation || '',
      icon: insight.icon || '',
      additionalCondition: insight.additionalCondition || ''
    });
    setOpenDialog(true);
  };

  const handleView = (insight) => {
    setSelectedInsight(insight);
    setOpenViewDialog(true);
  };

  const resetForm = () => {
    setSelectedInsight(null);
    setFormData({
      title: '',
      message: '',
      recommendation: '',
      icon: '',
      additionalCondition: ''
    });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };


  // Filter insights based on search
  const filteredInsights = insights.filter(insight => {
    const matchesSearch = !searchTerm || 
      insight.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insight.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (insight.recommendation && insight.recommendation.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Reset page when filtered results change
  useEffect(() => {
    const maxPage = Math.ceil(filteredInsights.length / rowsPerPage) - 1;
    if (page > maxPage && maxPage >= 0) {
      setPage(0);
    }
  }, [filteredInsights.length, rowsPerPage, page]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            AI Insights Management
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Configure nutrition insights that OpenAI will intelligently select and display based on each user's dietary patterns
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Create New Insight
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Insights
              </Typography>
              <Typography variant="h5" component="div">
                {statistics.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Insights
              </Typography>
              <Typography variant="h5" component="div" color="success.main">
                {statistics.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Insight Types
              </Typography>
              <Typography variant="h5" component="div">
                {Object.keys(statistics.byType).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Categories
              </Typography>
              <Typography variant="h5" component="div">
                {Object.keys(statistics.byCategory).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={10}>
            <TextField
              fullWidth
              size="small"
              label="Search insights..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              placeholder="Search by title, message, or recommendation"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setPage(0);
              }}
            >
              Clear Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Insights Table */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          AI Insights List
        </Typography>
        {!loading && (
          <Typography variant="body2" color="text.secondary">
            Showing {Math.min((page + 1) * rowsPerPage, filteredInsights.length)} of {filteredInsights.length} insights
          </Typography>
        )}
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ position: 'relative' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Icon</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Recommendation</TableCell>
                <TableCell>Additional Condition</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInsights.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No insights found matching your filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredInsights.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No data on this page
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInsights.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((insight) => (
                <TableRow key={insight.id}>
                  <TableCell>
                    {insight.icon && <span style={{ fontSize: '1.5rem' }}>{insight.icon}</span>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {insight.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ maxWidth: 300 }} noWrap title={insight.message}>
                      {insight.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ maxWidth: 250 }} noWrap title={insight.recommendation || ''}>
                      {insight.recommendation || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ maxWidth: 200 }} noWrap title={insight.additionalCondition || ''}>
                      {insight.additionalCondition || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleView(insight)}
                      color="info"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(insight)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(insight.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredInsights.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedInsight ? 'Edit AI Insight' : 'Create New AI Insight'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Icon (Emoji)"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  helperText="Optional emoji icon for the insight"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  helperText="Short, attention-grabbing title (e.g., 'High Carb Intake Detected')"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  multiline
                  rows={3}
                  required
                  helperText="Explain what the data shows and why it matters"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Recommendation (Optional)"
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                  multiline
                  rows={2}
                  helperText="Actionable advice to help improve this metric"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Condition (Admin Only)"
                  value={formData.additionalCondition}
                  onChange={(e) => setFormData({ ...formData, additionalCondition: e.target.value })}
                  multiline
                  rows={2}
                  helperText="Optional condition that must be met for this insight to be shown (e.g., 'avgCarbs > 200' or 'dietPreferences includes vegetarian')"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); resetForm(); }} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateOrUpdate} 
            variant="contained" 
            color="primary"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : (selectedInsight ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>AI Insight Details</DialogTitle>
        <DialogContent>
          {selectedInsight && (
            <Box sx={{ mt: 2 }}>
              <Tabs value={languageTab} onChange={(e, v) => setLanguageTab(v)} sx={{ mb: 2 }}>
                <Tab label="English" value="english" />
                {selectedInsight.languageSpec && Object.keys(selectedInsight.languageSpec).map(lang => (
                  <Tab key={lang} label={lang.charAt(0).toUpperCase() + lang.slice(1)} value={lang} />
                ))}
              </Tabs>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Title:</Typography>
                  <Typography variant="body1">
                    {languageTab === 'english' 
                      ? selectedInsight.title 
                      : selectedInsight.languageSpec?.[languageTab]?.title || selectedInsight.title}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Message:</Typography>
                  <Typography variant="body1">
                    {languageTab === 'english' 
                      ? selectedInsight.message 
                      : selectedInsight.languageSpec?.[languageTab]?.message || selectedInsight.message}
                  </Typography>
                </Grid>
                {selectedInsight.recommendation && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Recommendation:</Typography>
                    <Typography variant="body1">
                      {languageTab === 'english' 
                        ? selectedInsight.recommendation 
                        : selectedInsight.languageSpec?.[languageTab]?.recommendation || selectedInsight.recommendation}
                    </Typography>
                  </Grid>
                )}
                {selectedInsight.additionalCondition && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Additional Condition (Admin Only):</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', padding: 1, borderRadius: 1 }}>
                      {selectedInsight.additionalCondition}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AIInsightsManager;